import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminNavigation from '@/components/AdminNavigation';
import { ShoppingCart, Loader2 } from 'lucide-react';

// Local components
import { StatusCards } from './StatusCards';
import { OrdersTable } from './OrdersTable';
import { OrderDetailsModal } from './OrderDetailsModal';
import { API, getStatusCounts } from './helpers';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/sample-orders`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Kunne ikkje laste bestillingar');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status, adminRemarks = '') => {
    try {
      setProcessing(true);
      await axios.put(
        `${API}/sample-orders/${orderId}`,
        { status, admin_remarks: adminRemarks },
        { withCredentials: true }
      );
      toast.success(`Bestilling ${status === 'approved' ? 'godkjent' : status === 'rejected' ? 'avslått' : 'oppdatert'}`);
      fetchOrders();
      setShowDetailsModal(false);
      setSelectedOrder(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Kunne ikkje oppdatere bestilling');
    } finally {
      setProcessing(false);
    }
  };

  const openDetailsModal = (order) => {
    setSelectedOrder(order);
    setAdminNotes(order.admin_remarks || '');
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedOrder(null);
    setAdminNotes('');
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const counts = getStatusCounts(orders);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <AdminNavigation />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Lastar bestillingar...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminNavigation />
      <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Bestillingar</h1>
            <p className="text-sm text-slate-500 mt-1">Handter innkomande bestillingar frå kundar</p>
          </div>

          <StatusCards 
            filter={filter} 
            setFilter={setFilter} 
            counts={counts} 
          />

          <OrdersTable 
            orders={filteredOrders} 
            onOpenDetails={openDetailsModal} 
          />

          {showDetailsModal && (
            <OrderDetailsModal
              order={selectedOrder}
              adminNotes={adminNotes}
              setAdminNotes={setAdminNotes}
              processing={processing}
              onClose={closeDetailsModal}
              onUpdateStatus={updateOrderStatus}
            />
          )}
        </div>
      </main>
    </div>
  );
}
